
import { supabase } from "@/integrations/supabase/client";
import { isPwaMode, isIOSDevice } from "@/utils/pwaUtils";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

/**
 * Checks if a token is a valid Supabase JWT
 * @param token The token to check
 * @returns True if the token is a valid JWT format
 */
function isValidJWT(token: string): boolean {
  try {
    // Check basic JWT structure (three segments separated by periods)
    const parts = token.split('.');
    if (parts.length !== 3) {
      logger.info("Token does not have three parts separated by periods");
      return false;
    }

    // Check that each part is valid base64
    const isBase64 = (str: string) => {
      try {
        // Add padding if needed
        let paddedStr = str;
        while (paddedStr.length % 4 !== 0) {
          paddedStr += '=';
        }
        // Replace URL-safe chars
        paddedStr = paddedStr.replace(/-/g, '+').replace(/_/g, '/');
        // Try to decode
        return !!atob(paddedStr);
      } catch (e) {
        return false;
      }
    };

    // Verify each part is valid base64
    if (!isBase64(parts[0]) || !isBase64(parts[1])) {
      logger.info("Token parts are not valid base64");
      return false;
    }

    // Try to parse the payload
    try {
      const payload = JSON.parse(atob(
        parts[1].replace(/-/g, '+').replace(/_/g, '/')
      ));

      // Check for minimum required JWT claims
      if (!payload.sub || !payload.exp) {
        logger.info("Token missing required JWT claims");
        return false;
      }

      return true;
    } catch (e) {
      logger.info("Failed to parse token payload");
      return false;
    }
  } catch (e) {
    logger.error("Error validating JWT:", e);
    return false;
  }
}

/**
 * Exchanges tokens with the token-exchange endpoint
 * @param token The token to exchange
 * @param email Optional user email
 * @param userId Optional user ID
 * @returns The exchanged token data
 */
async function exchangeToken(token: string, email?: string, userId?: string) {
  logger.info("Exchanging token with token-exchange endpoint");

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/google-auth/token-exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        googleToken: token,
        email,
        userId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    logger.info("Token exchange successful");
    return data;
  } catch (error) {
    logger.error("Error exchanging token:", error);
    throw error;
  }
}

/**
 * This function attempts to initialize auth for iOS PWA from localStorage or cookies
 * It should be called as early as possible in the app lifecycle
 */
export const initializeIOSPwaAuth = async (): Promise<boolean> => {
  // Only run on iOS PWA
  if (!isIOSDevice() || !isPwaMode()) {
    return false;
  }

  logger.info("Initializing iOS PWA auth recovery");

  try {
    // Try to recover from localStorage first
    const accessToken = localStorage.getItem('sb-access-token-backup');
    const refreshToken = localStorage.getItem('sb-refresh-token-backup');
    const userEmail = localStorage.getItem('user-email-backup');
    const userId = localStorage.getItem('user-id-backup');

    if (accessToken) {
      logger.info("Found backup tokens in localStorage for iOS PWA auth init");

      try {
        // Validate token format before attempting to use it
        if (isValidJWT(accessToken)) {
          logger.info("Backup token appears to be a valid JWT, using directly");

          // First try to use the tokens directly 
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          });

          if (!error && data.session) {
            logger.info("Successfully initialized iOS PWA auth from localStorage");
            return true;
          } else {
            logger.info("Direct token setting failed:", error?.message);
          }
        }

        // If token is not valid JWT or direct setting failed, exchange token
        logger.info("Trying token exchange...");
        const exchangedTokens = await exchangeToken(accessToken, userEmail || undefined, userId || undefined);

        // Now try to set the session with the exchanged tokens
        const { data: exchangeData, error: exchangeError } = await supabase.auth.setSession({
          access_token: exchangedTokens.access_token,
          refresh_token: exchangedTokens.refresh_token || ''
        });

        if (!exchangeError && exchangeData.session) {
          logger.info("Successfully initialized iOS PWA auth after token exchange");

          // Update localStorage with valid tokens
          try {
            localStorage.setItem('sb-access-token-backup', exchangedTokens.access_token);
            localStorage.setItem('sb-refresh-token-backup', exchangedTokens.refresh_token);
            localStorage.setItem('token-backup-timestamp', Date.now().toString());
          } catch (e) {
            logger.error("Error updating localStorage with exchanged tokens:", e);
          }

          return true;
        } else {
          throw new Error(`Session setting failed after token exchange: ${exchangeError?.message}`);
        }
      } catch (e) {
        logger.error("Error setting session with localStorage tokens:", e);
      }
    }

    // Try to recover from cookies
    const cookies = document.cookie.split(';');
    let cookieAccessToken, cookieRefreshToken, cookieEmail, cookieUserId;

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'sb-access-token-direct') {
        cookieAccessToken = decodeURIComponent(value);
      }
      if (name === 'sb-refresh-token-direct') {
        cookieRefreshToken = decodeURIComponent(value);
      }
      if (name === 'user-email') {
        cookieEmail = decodeURIComponent(value);
      }
      if (name === 'user-id') {
        cookieUserId = decodeURIComponent(value);
      }

      // Check for bundled token in iOS minimal cookie
      if (name === 'ios_auth_minimal' || name === 'ios_minimal_auth') {
        try {
          const minimalAuth = JSON.parse(decodeURIComponent(value));
          if (minimalAuth.a) cookieAccessToken = minimalAuth.a;
          if (minimalAuth.r) cookieRefreshToken = minimalAuth.r;
          if (minimalAuth.e) cookieEmail = minimalAuth.e;
          if (minimalAuth.u) cookieUserId = minimalAuth.u;
        } catch (e) {
          logger.error("Error parsing iOS minimal auth cookie:", e);
        }
      }

      // Check for bundled tokens in PWA token bundle
      if (name === 'pwa_token_bundle') {
        try {
          const bundle = JSON.parse(decodeURIComponent(value));
          if (bundle.a) cookieAccessToken = bundle.a;
          if (bundle.r) cookieRefreshToken = bundle.r;
          if (bundle.e) cookieEmail = bundle.e;
          if (bundle.u) cookieUserId = bundle.u;
        } catch (e) {
          logger.error("Error parsing PWA token bundle cookie:", e);
        }
      }
    }

    if (cookieAccessToken) {
      logger.info("Found tokens in cookies for iOS PWA auth init");

      try {
        // Validate token format before attempting to use it
        if (isValidJWT(cookieAccessToken)) {
          logger.info("Cookie token appears to be a valid JWT, using directly");

          // First try to use the tokens directly
          const { data, error } = await supabase.auth.setSession({
            access_token: cookieAccessToken,
            refresh_token: cookieRefreshToken || ''
          });

          if (!error && data.session) {
            logger.info("Successfully initialized iOS PWA auth from cookies");
            // Store in localStorage as backup
            try {
              localStorage.setItem('sb-access-token-backup', cookieAccessToken);
              if (cookieRefreshToken) {
                localStorage.setItem('sb-refresh-token-backup', cookieRefreshToken);
              }
              localStorage.setItem('token-backup-timestamp', Date.now().toString());
            } catch (e) {
              logger.error("Error storing tokens in localStorage during init:", e);
            }
            return true;
          } else {
            logger.info("Direct cookie token setting failed:", error?.message);
          }
        }

        // If token is not valid JWT or direct setting failed, exchange token
        logger.info("Trying token exchange for cookie tokens...");
        const exchangedTokens = await exchangeToken(cookieAccessToken, cookieEmail || userEmail || undefined, cookieUserId || userId || undefined);

        // Now try to set the session with the exchanged tokens
        const { data: exchangeData, error: exchangeError } = await supabase.auth.setSession({
          access_token: exchangedTokens.access_token,
          refresh_token: exchangedTokens.refresh_token || ''
        });

        if (!exchangeError && exchangeData.session) {
          logger.info("Successfully initialized iOS PWA auth after cookie token exchange");
          // Store in localStorage as backup
          try {
            localStorage.setItem('sb-access-token-backup', exchangedTokens.access_token);
            localStorage.setItem('sb-refresh-token-backup', exchangedTokens.refresh_token);
            localStorage.setItem('token-backup-timestamp', Date.now().toString());
          } catch (e) {
            logger.error("Error storing exchanged tokens in localStorage:", e);
          }
          return true;
        } else {
          throw new Error(`Session setting failed after cookie token exchange: ${exchangeError?.message}`);
        }
      } catch (e) {
        logger.error("Error setting session from cookies:", e);
      }
    }

    // Try alternative method for iOS PWA - check for fragment identifier
    if (window.location.hash && window.location.hash.includes('access_token=')) {
      logger.info("Found access token in URL hash fragment for iOS PWA");
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashAccessToken = hashParams.get('access_token');
        const hashRefreshToken = hashParams.get('refresh_token');
        const hashEmail = hashParams.get('user_email');
        const hashUserId = hashParams.get('user_id');

        if (hashAccessToken) {
          // Validate token format before attempting to use it
          if (isValidJWT(hashAccessToken)) {
            logger.info("Hash token appears to be a valid JWT, using directly");

            // Try to establish a session with these tokens
            try {
              const { data, error } = await supabase.auth.setSession({
                access_token: hashAccessToken,
                refresh_token: hashRefreshToken || ''
              });

              if (!error && data.session) {
                logger.info("Successfully initialized iOS PWA auth from URL hash");
                // Store in localStorage as backup
                try {
                  localStorage.setItem('sb-access-token-backup', hashAccessToken);
                  if (hashRefreshToken) {
                    localStorage.setItem('sb-refresh-token-backup', hashRefreshToken);
                  }
                  localStorage.setItem('token-backup-timestamp', Date.now().toString());

                  // Clean up the URL hash to avoid token exposure
                  if (window.history && window.history.replaceState) {
                    window.history.replaceState(null, '', window.location.pathname + window.location.search);
                  }
                } catch (e) {
                  logger.error("Error storing tokens from hash in localStorage:", e);
                }
                return true;
              } else {
                logger.info("Direct hash token setting failed:", error?.message);
              }
            } catch (e) {
              logger.error("Error setting session with hash tokens:", e);
            }
          }

          // If token is not valid JWT or direct setting failed, exchange token
          logger.info("Trying token exchange for hash tokens...");

          try {
            const exchangedTokens = await exchangeToken(hashAccessToken, hashEmail || userEmail || undefined, hashUserId || userId || undefined);

            // Now try to set the session with the exchanged tokens
            const { data: exchangeData, error: exchangeError } = await supabase.auth.setSession({
              access_token: exchangedTokens.access_token,
              refresh_token: exchangedTokens.refresh_token || ''
            });

            if (!exchangeError && exchangeData.session) {
              logger.info("Successfully initialized iOS PWA auth after hash token exchange");
              // Store in localStorage as backup
              try {
                localStorage.setItem('sb-access-token-backup', exchangedTokens.access_token);
                localStorage.setItem('sb-refresh-token-backup', exchangedTokens.refresh_token);
                localStorage.setItem('token-backup-timestamp', Date.now().toString());

                // Clean up the URL hash to avoid token exposure
                if (window.history && window.history.replaceState) {
                  window.history.replaceState(null, '', window.location.pathname + window.location.search);
                }
              } catch (e) {
                logger.error("Error storing exchanged tokens from hash in localStorage:", e);
              }
              return true;
            } else {
              throw new Error(`Session setting failed after hash token exchange: ${exchangeError?.message}`);
            }
          } catch (e) {
            logger.error("Error in token exchange process for hash tokens:", e);
          }
        }
      } catch (e) {
        logger.error("Error processing URL hash for iOS PWA auth:", e);
      }
    }

    logger.info("No valid tokens found for iOS PWA auth initialization");
    return false;
  } catch (e) {
    logger.error("Error in iOS PWA auth initialization:", e);
    return false;
  }
};

export default initializeIOSPwaAuth;
